import api, { API } from "@/lib/api";

const CHUNK = 8 * 1024 * 1024; // 8 MiB (multiple of 256 KiB)

// Uploads a file either directly to GCS (resumable, when configured) or via the
// backend proxy fallback (preview / no GCS key). Returns the stored attachment.
export async function uploadFile(file, scope, refId, onProgress) {
    const { data: init } = await api.post("/uploads/init", {
        scope,
        ref_id: refId || null,
        filename: file.name,
        content_type: file.type || "application/octet-stream",
        size: file.size,
    });

    if (init.mode === "gcs") {
        const sess = await fetch(init.signed_init_url, {
            method: "POST",
            headers: { "x-goog-resumable": "start", "Content-Type": init.content_type },
        });
        if (!sess.ok) throw new Error("Could not start the upload session — please retry");
        const sessionUrl = sess.headers.get("Location");
        if (!sessionUrl) throw new Error("Storage did not return an upload session");

        for (let start = 0; start < file.size;) {
            const end = Math.min(start + CHUNK, file.size);
            const res = await fetch(sessionUrl, {
                method: "PUT",
                headers: { "Content-Range": `bytes ${start}-${end - 1}/${file.size}`, "Content-Type": init.content_type },
                body: file.slice(start, end),
            });
            if (![200, 201, 308].includes(res.status)) throw new Error("Upload failed partway — please retry");
            start = end;
            if (onProgress) onProgress(Math.round((100 * start) / file.size));
        }
        const { data: att } = await api.post(`/uploads/${init.upload_id}/complete`);
        return att;
    }

    // Proxy fallback (preview / GCS not configured)
    const fd = new FormData();
    fd.append("file", file);
    const { data: att } = await api.post(`/uploads/${init.upload_id}/proxy`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => { if (onProgress && e.total) onProgress(Math.round((100 * e.loaded) / e.total)); },
    });
    return att;
}

export async function deleteUpload(id) {
    if (!id) return;
    try { await api.delete(`/uploads/${id}`); } catch { /* best-effort cleanup */ }
}

export function downloadHref(att) {
    const t = localStorage.getItem("qube_token");
    const q = t ? `?auth=${encodeURIComponent(t)}` : "";
    if (att.storage || !att.storage_path) return `${API}/uploads/${att.id}/download${q}`;
    return `${API}/queries/files/${att.storage_path}${q}`; // legacy object-store attachment
}
